import { CheckCircleSolid, MagnifyingGlass, SquareTwoStack, XMarkMini } from "@medusajs/icons"
import { clx, Tooltip } from "@medusajs/ui"
import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent, type ReactNode, type RefObject } from "react"
import { Link, NavLink } from "react-router-dom"
import { caps, t } from "../lib/i18n"

/**
 * HQ's own small pieces, shared by every page so the same thing always looks the same:
 * one kind of tab row, the ticket code, the figure tile, the size run.
 */

export type TabItem<K extends string> = {
  key: K
  label: ReactNode
  /** A number shown beside the label (how many in this queue). Left out while unknown. */
  count?: number | null
  /** A page of its own rather than a view of this one (the booking tabs). */
  to?: string
  end?: boolean
  /** Switching to it replaces the current entry in history, so Back leaves the section in one step. */
  replace?: boolean
}

/**
 * A row of tabs that scrolls sideways on a phone, without a scrollbar, fading at the edge that has
 * more, and bringing the chosen one into view.
 *  - "filter": pills — which orders of a list to show (To ship, To arrange…)
 *  - "page": underlined — switches between the views of one page (Sales, Customers…)
 * `fill` (page tabs): on a phone the tabs share the row in slightly smaller type, so a set that
 * nearly fits (Analytics' five) is all on screen instead of one tab scrolled out of sight.
 */
export function Tabs<K extends string>({
  items,
  value,
  onChange,
  variant = "filter",
  label,
  className,
  fill = false,
}: {
  items: Array<TabItem<K>>
  value?: K | null
  onChange?: (key: K) => void
  variant?: "filter" | "page"
  label: string
  className?: string
  fill?: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)
  useEdgeFade(ref)
  useLayoutEffect(() => {
    const row = ref.current
    const active = row?.querySelector<HTMLElement>("[aria-selected='true'], [aria-current='page']")
    if (!row || !active) return
    const left = active.offsetLeft - row.offsetLeft
    if (left < row.scrollLeft || left + active.offsetWidth > row.scrollLeft + row.clientWidth) {
      row.scrollLeft = Math.max(0, left - 24)
    }
  }, [value])

  const pill = (active: boolean) =>
    variant === "filter"
      ? clx(
          "txt-compact-small-plus inline-flex h-9 shrink-0 items-center gap-x-1.5 rounded-full border px-3.5 transition-colors",
          active ? "bg-ui-fg-base text-ui-bg-base border-transparent" : "bg-ui-bg-base text-ui-fg-subtle border-ui-border-base hover:text-ui-fg-base hover:border-ui-border-strong"
        )
      : clx(
          "txt-compact-medium-plus relative inline-flex h-11 shrink-0 items-center gap-x-1.5 px-0.5 transition-colors",
          fill && "max-sm:text-[13px]",
          // The underline stays INSIDE the tab: one pixel hanging below made the row scrollable
          // up and down, and iPhone let it bounce vertically while being swiped sideways.
          "after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:rounded-full",
          active ? "text-ui-fg-base after:bg-ui-fg-base" : "text-ui-fg-muted hover:text-ui-fg-subtle after:bg-transparent"
        )
  const count = (item: TabItem<K>, active: boolean) =>
    item.count == null ? null : (
      <span className={clx("hc-data text-[11px] leading-none", variant === "filter" ? (active ? "opacity-70" : "text-ui-fg-muted") : "text-ui-fg-muted")}>{item.count}</span>
    )

  return (
    <div
      ref={ref}
      role="tablist"
      aria-label={label}
      className={clx(
        "hc-hscroll -mx-4 flex px-4 lg:mx-0 lg:px-0",
        variant === "filter" ? "gap-x-1.5 py-0.5" : "gap-x-5 shadow-[inset_0_-1px_0_var(--border-base)]",
        variant === "page" && fill && "max-sm:justify-between max-sm:gap-x-3",
        className
      )}
    >
      {items.map((item) =>
        item.to ? (
          <NavLink key={item.key} to={item.to} end={item.end} replace={item.replace} role="tab" className={({ isActive }) => pill(isActive)}>
            {({ isActive }) => (
              <>
                {item.label}
                {count(item, isActive)}
              </>
            )}
          </NavLink>
        ) : (
          <button key={item.key} type="button" role="tab" aria-selected={value === item.key} onClick={() => onChange?.(item.key)} className={pill(value === item.key)}>
            {item.label}
            {count(item, value === item.key)}
          </button>
        )
      )}
    </div>
  )
}

/** Marks a sideways-scrolling row with data-fade=start|end|both while there is more to see that way. */
export function useEdgeFade(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const node = ref.current
    if (!node) return
    const update = () => {
      const more = node.scrollWidth - node.clientWidth
      const start = node.scrollLeft > 4
      const end = more - node.scrollLeft > 4
      const fade = start && end ? "both" : start ? "start" : end ? "end" : ""
      if (fade) node.dataset.fade = fade
      else delete node.dataset.fade
    }
    update()
    node.addEventListener("scroll", update, { passive: true })
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null
    observer?.observe(node)
    return () => {
      node.removeEventListener("scroll", update)
      observer?.disconnect()
    }
  }, [ref])
}

/** An order's code, printed like a stock ticket: W-46, C-1672, PO-3. */
export function Ref({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={clx("hc-ref", className)}>{children}</span>
}

type Tone = "default" | "ok" | "wait" | "sold" | "muted"
const TONE_TEXT: Record<Tone, string> = {
  default: "text-ui-fg-base",
  ok: "text-ui-tag-green-text",
  wait: "text-ui-tag-orange-text",
  sold: "text-ui-tag-red-text",
  muted: "text-ui-fg-muted",
}

/**
 * A headline figure: a printed label over the number, a line under it. With `to` it is the way into
 * the list it counts; with `onClick` it opens how it was worked out.
 */
export function Stat({
  label,
  value,
  hint,
  to,
  onClick,
  tone = "default",
  className,
  info,
}: {
  label: ReactNode
  value: ReactNode
  hint?: ReactNode
  to?: string
  onClick?: () => void
  tone?: Tone
  className?: string
  /** Shown top-right: a small "i" for figures that explain themselves when pressed. */
  info?: ReactNode
}) {
  const body = (
    <>
      <span className="flex items-start justify-between gap-x-2">
        <span className="hc-eyebrow">{typeof label === "string" ? caps(label) : label}</span>
        {info}
      </span>
      <span className={clx("hc-data mt-1.5 block text-[22px] leading-[26px]", TONE_TEXT[tone])}>{value}</span>
      {hint && <span className="txt-compact-xsmall text-ui-fg-muted mt-1 block">{hint}</span>}
    </>
  )
  const base = clx("bg-ui-bg-base shadow-elevation-card-rest flex min-w-0 flex-col rounded-lg p-3 text-left", className)
  if (to) {
    return (
      <Link to={to} className={clx(base, "hover:shadow-elevation-card-hover active:bg-ui-bg-base-pressed transition-shadow")}>
        {body}
      </Link>
    )
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={clx(base, "hover:shadow-elevation-card-hover active:bg-ui-bg-base-pressed transition-shadow")}>
        {body}
      </button>
    )
  }
  return <div className={base}>{body}</div>
}

/**
 * The size run — the stock ticket's row of sizes, each with what is on the shelf. A size with none
 * is printed faint and struck, so a sold-out size reads at a glance without a second colour.
 */
export function SizeRun({ sizes, className }: { sizes: Array<{ label: string; count: number | null }>; className?: string }) {
  return (
    <div className={clx("flex flex-wrap gap-1", className)}>
      {sizes.map((size) => {
        const out = size.count != null && size.count <= 0
        return (
          <span
            key={size.label}
            className={clx(
              "hc-data inline-flex h-7 max-w-full items-stretch overflow-hidden rounded-[3px] border text-[12px]",
              out ? "border-ui-border-base text-ui-fg-disabled" : "border-ui-fg-base text-ui-fg-base"
            )}
            title={size.count == null ? size.label : t("{size}: {count} on the shelf", { size: size.label, count: size.count })}
          >
            <span className={clx("flex min-w-0 items-center px-1.5", out && "line-through")}>
              <span className="truncate">{size.label}</span>
            </span>
            {size.count != null && (
              <span className={clx("flex min-w-[22px] shrink-0 items-center justify-center border-l px-1", out ? "border-ui-border-base" : "border-ui-fg-base bg-ui-fg-base text-ui-bg-base")}>
                {size.count}
              </span>
            )}
          </span>
        )
      })}
    </div>
  )
}

/** In stock / sold out / draft, printed like the label on a tag. Never wraps. */
export function StockState({ state, count }: { state: "in" | "out" | "draft"; count?: number }) {
  const text = caps(state === "draft" ? t("Draft") : state === "out" ? t("Sold out") : count == null ? t("In stock") : t.n(count, "{n} in stock", "{n} in stock"))
  return (
    <span
      className={clx(
        "hc-data inline-flex h-6 shrink-0 items-center gap-x-1.5 whitespace-nowrap rounded-full px-2 text-[11px] tracking-[0.04em]",
        state === "in" ? "bg-ui-tag-green-bg text-ui-tag-green-text" : state === "out" ? "bg-ui-tag-red-bg text-ui-tag-red-text" : "bg-ui-tag-neutral-bg text-ui-tag-neutral-text"
      )}
    >
      <span aria-hidden className={clx("size-1.5 rounded-full", state === "in" ? "bg-ui-tag-green-icon" : state === "out" ? "bg-ui-tag-red-icon" : "bg-ui-tag-neutral-icon")} />
      {text}
    </span>
  )
}

/** What an empty list says: what it means, and the way forward when there is one. */
export function Empty({ title, children, className }: { title: ReactNode; children?: ReactNode; className?: string }) {
  return (
    <div className={clx("border-ui-border-strong flex flex-col items-start gap-y-1 rounded-lg border border-dashed px-4 py-5", className)}>
      <p className="txt-compact-small-plus text-ui-fg-base">{title}</p>
      {children && <div className="txt-small text-ui-fg-muted">{children}</div>}
    </div>
  )
}

/** The search box at the top of a list: big enough for a thumb, with a clear button. */
export function SearchInput({ value, onChange, placeholder, label }: { value: string; onChange: (value: string) => void; placeholder: string; label?: string }) {
  return (
    <label className="bg-ui-bg-base shadow-elevation-card-rest focus-within:shadow-borders-interactive-with-active flex h-11 items-center gap-x-2 rounded-lg px-3">
      <MagnifyingGlass className="text-ui-fg-muted shrink-0" />
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={label ?? placeholder}
        enterKeyHint="search"
        autoComplete="off"
        className="txt-compact-medium placeholder:text-ui-fg-muted min-w-0 flex-1 bg-transparent outline-none [&::-webkit-search-cancel-button]:hidden"
      />
      {value && (
        <button type="button" onClick={() => onChange("")} aria-label={t("Clear the search")} className="text-ui-fg-muted hover:text-ui-fg-base -mr-1 rounded p-1">
          <XMarkMini />
        </button>
      )}
    </label>
  )
}

/** A card's title line, with its own buttons to the right. */
export function CardTitle({ children, action, hint }: { children: ReactNode; action?: ReactNode; hint?: ReactNode }) {
  return (
    <div className="flex flex-col gap-y-0.5">
      <div className="flex items-center justify-between gap-x-3">
        <h2 className="h2-core text-ui-fg-base min-w-0">{children}</h2>
        {action}
      </div>
      {hint && <p className="txt-small text-ui-fg-muted">{hint}</p>}
    </div>
  )
}

/**
 * The small copy icon with its tooltip — Medusa's <Copy>, whose tooltip says "Copy" / "Copied" in
 * English whatever the language; this one says it in the chosen language.
 */
export function CopyButton({ content, className }: { content: string; className?: string }) {
  const [done, setDone] = useState(false)
  const [open, setOpen] = useState(false)
  useEffect(() => {
    if (!done) return
    const timer = window.setTimeout(() => setDone(false), 2000)
    return () => window.clearTimeout(timer)
  }, [done])
  async function copy(event: MouseEvent) {
    event.stopPropagation()
    try {
      await navigator.clipboard.writeText(content)
      setDone(true)
    } catch {
      setOpen(true)
    }
  }
  return (
    <Tooltip content={done ? t("Copied") : t("Copy")} open={done || open} onOpenChange={setOpen}>
      <button type="button" aria-label={t("Copy")} onClick={(event) => void copy(event)} className={clx("h-fit w-fit", className)}>
        {done ? <CheckCircleSolid className="text-ui-fg-subtle" /> : <SquareTwoStack className="text-ui-fg-subtle" />}
      </button>
    </Tooltip>
  )
}
